/**
 * Settings, workdays (section 13.1; ADR 0146 decision 3; on Mappings → Techs
 * until ADR 0198): the days a tech is available, each in their own time zone.
 * Utilization's available hours are 8 on each of these days in a report's
 * range, for everyone (ADR 0201). Monday to Friday to start; no holiday
 * calendar.
 *
 * Each change saves as it's made, as the other settings do (ADR 0082): one
 * save at a time, a change made meanwhile sent once that's answered. The last
 * workday can't be unchecked, as the API refuses none. A refused change goes
 * back to what's saved, with the reason.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";

import { ApiError, retryUnlessRefused } from "../../api/client";
import type { AppSettings } from "../../api/types";
import { useSession } from "../../app/session-context";
import { useOneSaveAtATime } from "../../app/useOneSaveAtATime";
import { Panel } from "../../components/ui/Panel";

type Weekday = AppSettings["workdays"][number];

const WEEK: readonly { day: Weekday; short: string; name: string }[] = [
  { day: "mon", short: "Mon", name: "Monday" },
  { day: "tue", short: "Tue", name: "Tuesday" },
  { day: "wed", short: "Wed", name: "Wednesday" },
  { day: "thu", short: "Thu", name: "Thursday" },
  { day: "fri", short: "Fri", name: "Friday" },
  { day: "sat", short: "Sat", name: "Saturday" },
  { day: "sun", short: "Sun", name: "Sunday" },
];

const HEADING = "workdays-heading";

function WorkdaysForm({ settings }: { settings: AppSettings }) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const hintId = useId();
  const lastId = useId();
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: (workdays: Weekday[]) => api.updateSettings({ workdays }),
    onSuccess: (result) => {
      queryClient.setQueryData(["settings"], result);
      setError(null);
      setSaved(true);
    },
    onError: (failure) => {
      const named = failure instanceof ApiError ? failure.fieldErrors.find((e) => e.field === "workdays") : undefined;
      setError(named?.message ?? (failure instanceof Error ? failure.message : "Something went wrong."));
      // A refusal may come from a change made elsewhere: show what's saved.
      if (failure instanceof ApiError && failure.status < 500) {
        return queryClient.invalidateQueries({ queryKey: ["settings"] });
      }
      return undefined;
    },
  });
  const { send, saving } = useOneSaveAtATime(save);

  // What's saved, from the cache: a save's answer is there at once.
  const current = queryClient.getQueryData<AppSettings>(["settings"]) ?? settings;
  const shown = saving?.value ?? current.workdays;

  function toggle(day: Weekday, on: boolean) {
    const next = WEEK.map((d) => d.day).filter((d) => (d === day ? on : shown.includes(d)));
    if (next.length === 0) {
      return;
    }
    setSaved(false);
    setError(null);
    send(next);
  }

  let status: string | undefined;
  if (saving !== null) {
    status = "Saving…";
  } else if (saved) {
    status = "Saved.";
  }

  return (
    <fieldset aria-labelledby={HEADING} aria-describedby={hintId}>
      <p id={hintId} className="mb-3 text-sm text-muted">
        Utilization counts 8 hours on each of these days, for everyone, in their own time zone. No holidays.
      </p>
      <div className="flex flex-wrap gap-2">
        {WEEK.map(({ day, short, name }) => {
          const on = shown.includes(day);
          const last = on && shown.length === 1;
          return (
            <label
              key={day}
              className={
                "flex h-[38px] items-center gap-2 rounded-pill border border-line px-4 text-sm " +
                (on ? "bg-brand-tint font-semibold text-brand-ink-on-tint" : "font-medium text-muted") +
                (last ? " cursor-not-allowed" : " cursor-pointer")
              }
            >
              <input
                type="checkbox"
                className="size-4 accent-brand"
                aria-label={name}
                aria-describedby={last ? lastId : undefined}
                checked={on}
                disabled={last}
                onChange={(event) => {
                  toggle(day, event.target.checked);
                }}
              />
              {short}
            </label>
          );
        })}
      </div>
      {shown.length === 1 ? (
        <p id={lastId} className="mt-2 text-sm text-muted">
          At least one day is a workday: utilization divides by the hours available.
        </p>
      ) : null}
      <p role="status" className="mt-2 text-sm font-medium text-status-logged-fg empty:hidden">
        {status}
      </p>
      {error ? (
        <p
          role="alert"
          className="mt-2 rounded-row bg-status-needs-client-bg px-3 py-1 text-sm text-status-needs-client-fg"
        >
          Not saved: {error}
        </p>
      ) : null}
    </fieldset>
  );
}

export function WorkdaysSettings() {
  const { api } = useSession();
  const settings = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.getSettings(),
    retry: retryUnlessRefused,
  });

  let body;
  // A failed reload keeps the form it already has.
  if (settings.error && !settings.data) {
    body = (
      <p role="alert" className="text-muted">
        The settings could not be loaded. Reload the page to try again.
      </p>
    );
  } else if (!settings.data) {
    body = (
      <p role="status" className="text-muted">
        Loading…
      </p>
    );
  } else {
    body = <WorkdaysForm settings={settings.data} />;
  }

  return (
    <Panel aria-labelledby={HEADING}>
      <h2 id={HEADING} className="text-lg font-semibold tracking-tight">
        Workdays
      </h2>
      <div className="mt-3">{body}</div>
    </Panel>
  );
}
